import React, { useState, useEffect } from 'react';
import { Container, List, Icon } from 'semantic-ui-react';
import ExpandableList from './ExpandableList';
import CardBackground from './CardBackground';
import snowflakeImg from '../images/snowflake.webp';
import awsImg from '../images/aws.jfif';
import petuumImg from '../images/petuum.png';
import citadelImg from '../images/citadel.jpg';
import johnDeereImg from '../images/john_deere.jfif';
import uiucImg from '../images/uiuc.jpg';
import iseImg from '../images/ise.jpg';
import githubImg from '../images/github.png';
import * as REAL_EN from '../data/en';
import * as REAL_CN from '../data/cn';

function GeneratingCursor({ text, animationDelay, paused }) {
  const [displayedLen, setDisplayedLen] = useState(0);
  const [phase, setPhase] = useState('typing'); // 'typing' | 'pausing'
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    query.addListener(update);
    return () => query.removeListener(update);
  }, []);

  useEffect(() => {
    if (paused || reducedMotion) return;
    if (phase === 'typing') {
      if (displayedLen < text.length) {
        const timer = setTimeout(() => {
          setDisplayedLen(prev => prev + 1);
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setPhase('pausing');
      }
    } else if (phase === 'pausing') {
      const timer = setTimeout(() => {
        setDisplayedLen(0);
        setPhase('typing');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [displayedLen, phase, text.length, paused, reducedMotion]);

  return (
    <div className="agent-cursor-line">
      <span className="agent-spinner" style={{ animationDelay, visibility: 'hidden' }} />
      <span className="cursor-placeholder">{paused || reducedMotion ? text : text.slice(0, displayedLen)}</span>
      <span className="cursor-blink" />
    </div>
  );
}

function ExperienceSection({ experiences, lang, isPublic, motionPaused }) {
  const EXP_BG_MAP = {
    'Snowflake': isPublic ? githubImg : snowflakeImg,
    'Amazon': isPublic ? githubImg : awsImg,
    'Petuum': isPublic ? githubImg : petuumImg,
    'Citadel': isPublic ? githubImg : citadelImg,
    'John Deere': isPublic ? githubImg : johnDeereImg,
    'Teaching Assistant': iseImg,
  };

  // In public mode, swap in real Teaching Assistant entry (last entry, index 5)
  let displayExperiences = experiences;
  if (isPublic) {
    const realExps = lang ? REAL_CN.experiences : REAL_EN.experiences;
    const realTA = realExps.find(e => e.header.includes('Teaching Assistant') || e.header.includes('助教'));
    if (realTA) {
      displayExperiences = [...experiences];
      // Find matching fake entry by index (TA is last entry in real data)
      const taIdx = realExps.indexOf(realTA);
      if (taIdx >= 0 && taIdx < displayExperiences.length) {
        displayExperiences[taIdx] = realTA;
      }
    }
  }

  function getExpBg(header) {
    for (const [key, img] of Object.entries(EXP_BG_MAP)) {
      if (header.includes(key)) return img;
    }
    return null;
  }

  return (
    <Container text textAlign="left">
        {displayExperiences.map((exp, idx) => {
          const isCurrent = exp.date && /present|至今/i.test(exp.date);
          const isTA = exp.header.includes('Teaching Assistant') || exp.header.includes('助教');
          const bgUrl = isPublic ? (isTA ? iseImg : githubImg) : getExpBg(exp.header);
          // AWS art has the wordmark off to the right of the map graphic — anchor on it
          const bgFocus = (!isPublic && /AWS|Amazon/i.test(exp.header)) ? { x: 0.74, y: 0.49 } : undefined;
          return (
          <div
            key={idx}
            className={`agent-window${bgUrl ? ' agent-window--bg-image' : ''}`}
          >
            {/* Title bar with traffic light dots */}
            <div className="agent-window__titlebar">
              <div className="agent-window__dots">
                <span className={`dot ${isCurrent ? 'dot--yellow' : 'dot--green'}`} style={{ animationDelay: `${idx * 0.5}s` }} />
              </div>
              <Icon name="briefcase" className="agent-window__icon" />
              <h3 className="agent-window__title">
                {exp.headerLink ? (
                  <a href={exp.headerLink} className="agent-window__link">{exp.header}<Icon name="external alternate" className="link-icon" /></a>
                ) : (
                  exp.header
                )}
                {exp.date && <span className="agent-window__date">{exp.date}</span>}
              </h3>
            </div>

            {/* Content area */}
            <div className="agent-window__content">
              {bgUrl && <CardBackground src={bgUrl} focus={bgFocus} />}
              {exp.type === 'expandable' && exp.items ? (
                <ExpandableList items={exp.items} />
              ) : exp.items ? (
                <React.Fragment>
                  <List as="ul">
                    {exp.items.map((item, i) => (
                        <List.Item key={i}>
                          <span className="agent-spinner" style={{ animationDelay: `${i * 0.5}s` }} />
                          <span>{item}</span>
                        </List.Item>
                      ))}
                  </List>
                  {isCurrent && (
                    <>
                      <div className="agent-divider" />
                      <GeneratingCursor paused={motionPaused} text={lang ? '生成中...' : 'Generating...'} animationDelay={`${exp.items.length * 0.5}s`} />
                    </>
                  )}
                </React.Fragment>
              ) : null}
            </div>
          </div>
          );
        })}
    </Container>
  );
}

export default ExperienceSection;
